import { tradePromoOptimizationUrls } from 'config/urls';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Interface for API response
export interface OptimizeOption {
    label: string;
    value: string;
    disabled: boolean;
    constraint_value: string;
    constraint_label: string;
}

// Custom hook to fetch optimize options with retry mechanism
export const useOptimizeOptions = () => {
    const {
        data: apiOptimizeOptions,
        isLoading: optimizeOptionsLoading,
        error,
    } = useQuery({
        queryKey: ['tpo-optimize-options'],
        queryFn: async () => {
            const response = await axios({
                method: 'get',
                url: tradePromoOptimizationUrls.getObjectivesWithDefaultConstraints,
            });

            // Transform API response to match the expected format
            return response.data.map((option: OptimizeOption) => ({
                label: option.label,
                value: option.value,
                disabled: option.disabled,
                constraintKey: option.constraint_value,
            }));
        },
        retry: 3, // Retry up to 3 times on any failure
        retryDelay: (attemptIndex) => {
            // Exponential backoff: 1s, 2s, 4s
            return Math.min(1000 * Math.pow(2, attemptIndex), 5000);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    return {
        optimizeOptions: apiOptimizeOptions || [],
        loading: optimizeOptionsLoading,
        error,
    };
};
